import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable } from "../components/Table";
import { DownloadSimple, FilePdf, MagnifyingGlass } from "@phosphor-icons/react";

const DATE_PRESETS = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days"];

export default function Reports() {
  const [catalog, setCatalog] = useState([]);
  const [report, setReport] = useState({ id: "live-status", label: "Live Vehicle Status" });
  const [vehicles, setVehicles] = useState([]);
  const [vsel, setVsel] = useState([]);
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("all");
  const [preset, setPreset] = useState("Today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/reports/catalog").then((r) => setCatalog(r.data.catalog));
    api.get("/vehicles").then((r) => setVehicles(r.data.vehicles));
    api.get("/groups").then((r) => setGroups(r.data.groups)).catch(() => {});
  }, []);

  const pickGroup = (gid) => {
    setGroup(gid);
    if (gid === "all") { setVsel([]); return; }
    const ids = new Set((groups.find((g) => g.id === gid)?.vehicle_ids) || []);
    setVsel(vehicles.filter((v) => ids.has(v.id)).map((v) => v.vehicle_number));
  };

  const run = () => {
    setLoading(true);
    api.get(`/reports/${report.id}`, { params: { vehicle: vsel.length ? vsel.join(",") : "all" } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to generate report"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [report.id, vsel.join(",")]);

  const rows = (data?.rows || []).filter((row) =>
    !search || Object.values(row).some((v) => String(v).toLowerCase().includes(search.toLowerCase()))
  );

  const exportCsv = () => {
    if (!data?.rows?.length) return toast.error("No data to export");
    const keys = data.columns.map((c) => c.key);
    const headers = data.columns.map((c) => c.header);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...data.rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${report.id}-${Date.now()}.csv`; a.click();
    toast.success("CSV exported");
  };

  const exportPdf = () => {
    if (!data?.rows?.length) return toast.error("No data to export");
    const w = window.open("", "_blank");
    const th = data.columns.map((c) => `<th>${c.header}</th>`).join("");
    const tr = data.rows.map((r) => `<tr>${data.columns.map((c) => `<td>${r[c.key] ?? ""}</td>`).join("")}</tr>`).join("");
    w.document.write(`<html><head><title>${report.label}</title><style>
      body{font-family:Arial;padding:24px;color:#111}h1{font-size:18px}
      .meta{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:11px}
      th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}
      th{background:#f2f2f2}</style></head><body>
      <h1>Velvet AIS-140 — ${report.label}</h1>
      <div class="meta">Vehicles: ${vsel.length ? vsel.join(", ") : "All"} · Period: ${preset} · Generated: ${new Date().toLocaleString()} · ${data.rows.length} rows</div>
      <table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
    toast.success("Opening print / PDF view");
  };

  const columns = (data?.columns || []).map((c) => ({ key: c.key, header: c.header }));

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Reports" subtitle="Professional GPS fleet reports · CSV & PDF export"
        actions={<div className="flex gap-2">
          <button data-testid="export-csv" onClick={exportCsv} className="flex items-center gap-1.5 bg-secondary text-white text-sm font-semibold px-3 py-2 rounded-sm hover:bg-secondary/80"><DownloadSimple size={15} weight="bold" /> Excel</button>
          <button data-testid="export-pdf" onClick={exportPdf} className="flex items-center gap-1.5 bg-secondary text-white text-sm font-semibold px-3 py-2 rounded-sm hover:bg-secondary/80"><FilePdf size={15} weight="bold" /> PDF</button>
        </div>} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Report catalog sidebar */}
        <div className="bg-card border border-border rounded-sm p-3 h-[620px] overflow-y-auto" data-testid="report-catalog">
          {catalog.map((g) => (
            <div key={g.group} className="mb-3">
              <div className="label-caps mb-1">{g.group}</div>
              {g.reports.map(([id, label]) => (
                <button key={id} data-testid={`report-${id}`} onClick={() => setReport({ id, label })}
                  className={`w-full text-left text-sm px-2 py-1.5 rounded-sm ${report.id === id ? "bg-primary/15 text-white font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>
                  {label}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Report content */}
        <div className="lg:col-span-3 space-y-3">
          {/* filter bar */}
          <div className="bg-card border border-border rounded-sm p-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="label-caps">Date Range</label>
              <div className="flex gap-1 mt-1">
                {DATE_PRESETS.map((p) => (
                  <button key={p} data-testid={`preset-${p.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setPreset(p)}
                    className={`text-xs font-semibold px-2 py-1.5 rounded-sm border ${preset === p ? "border-primary bg-primary/15" : "border-border text-muted-foreground"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-caps">Group</label>
              <select data-testid="report-group" value={group} onChange={(e) => pickGroup(e.target.value)}
                className="mt-1 block bg-background border border-border rounded-sm px-2 py-1.5 text-sm min-w-[150px]">
                <option value="all">All Groups</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.vehicle_ids.length})</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Vehicles <span className="text-[9px] normal-case">(none = all · ctrl/cmd for multiple)</span></label>
              <select multiple data-testid="report-vehicle" value={vsel}
                onChange={(e) => { setGroup("all"); setVsel(Array.from(e.target.selectedOptions).map((o) => o.value)); }}
                className="mt-1 block bg-background border border-border rounded-sm px-2 py-1.5 text-sm min-w-[180px] h-[76px]">
                {vehicles.map((v) => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="label-caps">Search</label>
              <div className="relative mt-1">
                <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
                <input data-testid="report-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter rows"
                  className="w-full bg-background border border-border rounded-sm pl-7 pr-2 py-1.5 text-sm" />
              </div>
            </div>
            <button data-testid="run-report" onClick={run} className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm">Generate</button>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-lg">{report.label}</h2>
            <span className="text-xs text-muted-foreground">{loading ? "Generating…" : `${rows.length} rows`}</span>
          </div>

          <DataTable testid="report-table" columns={columns} rows={rows} empty={loading ? "Generating…" : "No records for this filter"} />
        </div>
      </div>
    </div>
  );
}
